import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/primitives/Dialog';
import type { AICaseRecipe } from '../constants/subjectCatalog';
import {
  EXAM_LEVEL_OPTIONS,
  RECIPE_DIFFICULTY_OPTIONS,
  type RecipeAiBuildOptions,
  type RecipeDifficultyId,
} from '../constants/recipeAiParams';
import type { ExamLevelId } from '../constants/examAiParams';
import { MusicMethodologyPanel } from './MusicMethodologyPanel';
import { DEFAULT_MUSIC_AI_PREFS } from '../constants/musicAiParams';
import type { MusicGenerationPrefs } from '@interactive-room';
import { requireMaterialLevelAndSubject } from '../lib/courseProfileValidation';

interface RecipeAiBuildDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipe: AICaseRecipe | null;
  subjectLabel?: string;
  subjectId?: string;
  /** Tag canónico del curso — sin inventar 'general'/'matematica'. */
  subjectTag?: string;
  /** Nivel del curso; vacío si no está configurado. */
  courseLevel?: ExamLevelId | '';
  loading?: boolean;
  onConfirm: (options: RecipeAiBuildOptions) => void;
}

export function RecipeAiBuildDialog({
  open,
  onOpenChange,
  recipe,
  subjectLabel,
  subjectId,
  subjectTag: subjectTagProp,
  courseLevel,
  loading = false,
  onConfirm,
}: RecipeAiBuildDialogProps) {
  const subjectTag = subjectTagProp ?? subjectId ?? '';
  const [level, setLevel] = useState<ExamLevelId | ''>(() => courseLevel || '');
  const [difficulty, setDifficulty] = useState<RecipeDifficultyId>('medium');
  const [tuning, setTuning] = useState('');
  const [musicPrefs, setMusicPrefs] = useState<MusicGenerationPrefs>(DEFAULT_MUSIC_AI_PREFS);
  const isMusic = subjectTag === 'musica' || subjectId === 'musica' || recipe?.subjectId === 'musica';

  useEffect(() => {
    if (open) {
      setTuning('');
      setMusicPrefs(DEFAULT_MUSIC_AI_PREFS);
      setLevel(courseLevel || '');
    }
  }, [open, recipe?.id, courseLevel]);

  const identityGate = requireMaterialLevelAndSubject({
    level,
    subjectTag,
  });
  const profileIdentityReady = identityGate.ok;
  const canSubmit = !loading && profileIdentityReady;

  if (!recipe) return null;

  const handleConfirm = () => {
    if (!identityGate.ok) return;
    onConfirm({
      level: identityGate.level,
      difficulty,
      tuning: tuning.trim(),
      ...(isMusic ? { musicPrefs } : {}),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sala-dialog--studio sala-dialog--ai">
        <DialogHeader>
          <DialogTitle>Vamos a generar: {recipe.title}</DialogTitle>
          <DialogDescription>
            {subjectLabel ? (
              <>
                La IA preparará el juego para <strong>{subjectLabel}</strong>. Ajusta nivel y dificultad;
                el detalle es opcional.
              </>
            ) : (
              <>Configura nivel y dificultad. Puedes dejar el detalle en blanco para generación automática.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="sala-dialog__body space-y-5">
          <div className="rounded-xl border border-[var(--sala-border-strong)] bg-[var(--sala-surface-raised)] px-4 py-3">
            <p className="text-sm font-semibold text-[var(--sala-ink)]">{recipe.title}</p>
            <p className="mt-1 text-xs text-[var(--sala-ink-dim)]">{recipe.description}</p>
          </div>

          <label className="block">
            <span className="sala-label">Nivel educativo</span>
            <select
              className="sala-input-dark mt-2 w-full text-sm"
              value={level}
              onChange={(e) => setLevel(e.target.value as ExamLevelId | '')}
            >
              <option value="">Selecciona nivel…</option>
              {EXAM_LEVEL_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="sala-label">Dificultad</span>
            <select
              className="sala-input-dark mt-2 w-full text-sm"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as RecipeDifficultyId)}
            >
              {RECIPE_DIFFICULTY_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[10px] text-[var(--sala-ink-dim)]">
              {RECIPE_DIFFICULTY_OPTIONS.find((o) => o.id === difficulty)?.hint}
            </p>
          </label>

          <label className="block">
            <span className="sala-label">Especifica contenido (opcional)</span>
            <textarea
              className="sala-input-dark mt-2 min-h-[5rem] w-full resize-y text-sm"
              value={tuning}
              onChange={(e) => setTuning(e.target.value)}
              placeholder={recipe.placeholderText}
            />
          </label>

          {isMusic && (
            <MusicMethodologyPanel value={musicPrefs} onChange={setMusicPrefs} />
          )}

          {!canSubmit && !loading ? (
            <p className="ai-build-dialog__validation" role="status">
              {identityGate.ok
                ? 'Completa los campos requeridos para generar.'
                : identityGate.message}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <button type="button" className="sala-btn-outline text-xs" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </button>
          <button
            type="button"
            className="sala-btn-hero text-xs"
            disabled={!canSubmit}
            onClick={handleConfirm}
          >
            {loading ? 'Generando…' : 'Generar juego con IA'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
